import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { liveQuery, type Subscription } from "dexie";

import { Uuid } from "@/domains/global/identifiers";
import { logger } from "@/modules/logger";
import type { SpaceAccountNoteAttachmentObservable } from "@/store/note-attachments/SpaceAccountNoteAttachmentObservable";
import type { AppStoreSpaceAccountNoteAttachmentsStore } from "@/store/note-attachments/AppStoreSpaceAccountNoteAttachmentsStore";

type ConstructorArgs = {
  store: AppStoreSpaceAccountNoteAttachmentsStore;
  noteId: Uuid;
};

export class NoteAttachmentsObservable {
  private store: AppStoreSpaceAccountNoteAttachmentsStore;
  private noteId: Uuid;
  private subscription?: Subscription;

  attachmentIds: Uuid[];
  isLoading: boolean;

  constructor({ store, noteId }: ConstructorArgs) {
    this.store = store;
    this.noteId = noteId;
    this.attachmentIds = [];
    this.isLoading = true;

    makeObservable<this, "store" | "noteId" | "subscription">(this, {
      store: false,
      noteId: observable,
      subscription: observable,
      attachmentIds: observable,
      isLoading: observable,
      attachments: computed,
      subscribe: action,
      unsubscribe: action,
      dispose: action,
    });

    onBecomeObserved(this, "attachments", () => this.subscribe());
    onBecomeUnobserved(this, "attachments", () => this.unsubscribe());
  }

  get attachments(): SpaceAccountNoteAttachmentObservable[] {
    const results: SpaceAccountNoteAttachmentObservable[] = [];
    for (const id of this.attachmentIds) {
      const model = this.store.get(id);
      if (model) results.push(model);
    }
    return results;
  }

  subscribe() {
    this.unsubscribe();

    runInAction(() => {
      this.isLoading = true;
    });

    const targetNoteId = this.noteId;

    this.subscription = liveQuery(async () => {
      const records = await this.store.localTable
        .where("associated_note_id")
        .equals(targetNoteId)
        .toArray();

      return records.map(r => r.model_id);
    }).subscribe({
      next: ids => {
        runInAction(() => {
          this.attachmentIds = ids;
          this.isLoading = false;
        });
      },
      error: err => {
        logger.error({
          message: `[NoteAttachmentsObservable] liveQuery error for noteId=${targetNoteId}`,
          info: { error: String(err) },
        });
        runInAction(() => {
          this.attachmentIds = [];
          this.isLoading = false;
        });
      },
    });
  }

  unsubscribe() {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  dispose() {
    this.unsubscribe();
  }
}
